import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--combo-box"
// Single-select combo box: a select-looking trigger that opens a popover with a
// search input at the top and a client-side-filtered option list below. A 1:1
// behavioural replica of the @a-safe-digital React ComboBox (default variant).
// Data-driven: all options are rendered up-front and shown/hidden while filtering.
export default class extends Controller {
  static targets = [
    "trigger",
    "display",
    "panel",
    "search",
    "clearSearch",
    "option",
    "empty",
    "hidden",
  ]

  static values = {
    showSearch: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    emptyLabel: { type: String, default: "No results found" },
  }

  // Selected-state class tokens — must mirror ComboBoxComponent#option_classes.
  SELECTED_CLASSES = ["bg-primary/20!", "text-primary", "hover:bg-primary/20!"]
  UNSELECTED_CLASS = "hover:bg-input"

  connect() {
    this._isOpen = false
    this._activeIndex = -1

    this._outsideClickHandler = this._handleOutsideClick.bind(this)
    this._keydownHandler = this._handleKeydown.bind(this)
    document.addEventListener("click", this._outsideClickHandler)
    document.addEventListener("keydown", this._keydownHandler)
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClickHandler)
    document.removeEventListener("keydown", this._keydownHandler)
  }

  // --- Open / close ---

  toggle() {
    if (this.disabledValue) return
    if (this._isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.disabledValue || this._isOpen) return
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.triggerTarget.dataset.state = "open"
    this._isOpen = true
    this._activeIndex = -1
    this._resetFilter()
    this._position()

    if (this.showSearchValue && this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  // Flip the panel above the trigger when there isn't room below it, AND cap the
  // scrollable option list to the space actually available on the chosen side.
  // Space is measured against the nearest CLIPPING ancestor (e.g. the modal body
  // that hosts the booking form), NOT the viewport: the dropdown is clipped by
  // that container, so a viewport-based calc wrongly thinks there's room below
  // and leaves the list clipped to a couple of unreachable rows (CONEK-4230 QA).
  _position() {
    const panel = this.panelTarget
    panel.classList.remove("bottom-full", "mb-2", "top-full", "mt-2")
    const margin = 16
    const rect = this.triggerTarget.getBoundingClientRect()
    const clip = this._clipRect()
    const spaceBelow = clip.bottom - rect.bottom - margin
    const spaceAbove = rect.top - clip.top - margin
    const flipUp = spaceBelow < panel.offsetHeight && spaceAbove > spaceBelow
    panel.classList.add(
      ...(flipUp ? ["bottom-full", "mb-2"] : ["top-full", "mt-2"]),
    )

    const list = panel.querySelector('[role="listbox"]')
    if (list) {
      const available = flipUp ? spaceAbove : spaceBelow
      // Height taken by the non-scrolling chrome (search box + paddings).
      const chrome = Math.max(0, panel.offsetHeight - list.offsetHeight)
      const listMax = Math.max(120, available - chrome)
      list.style.maxHeight = `${Math.min(380, listMax)}px`
    }
  }

  // Bounds of the nearest scroll/clip ancestor (the modal body, a scroll pane,
  // …), clamped to the viewport. Falls back to the viewport when the dropdown
  // isn't nested inside a clipping container.
  _clipRect() {
    let el = this.panelTarget.parentElement
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY
      if (oy === "auto" || oy === "scroll" || oy === "hidden") {
        const r = el.getBoundingClientRect()
        return {
          top: Math.max(0, r.top),
          bottom: Math.min(window.innerHeight, r.bottom),
        }
      }
      el = el.parentElement
    }
    return { top: 0, bottom: window.innerHeight }
  }

  close() {
    if (!this._isOpen) return
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.triggerTarget.dataset.state = "closed"
    this._isOpen = false
    this._clearActive()
  }

  // --- Filtering (client-side) ---

  filter() {
    const query = this.hasSearchTarget
      ? this.searchTarget.value.trim().toLowerCase()
      : ""

    let visibleCount = 0
    this.optionTargets.forEach((option) => {
      const label = (option.dataset.label || "").toLowerCase()
      const match = label.includes(query)
      option.style.display = match ? "" : "none"
      if (match) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }
    if (this.hasClearSearchTarget) {
      this.clearSearchTarget.classList.toggle("hidden", query.length === 0)
    }
    // Reset keyboard cursor whenever the visible set changes.
    this._activeIndex = -1
    this._clearActive()
  }

  clearSearch() {
    if (!this.hasSearchTarget) return
    this.searchTarget.value = ""
    this.filter()
    this.searchTarget.focus()
  }

  _resetFilter() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.optionTargets.forEach((option) => {
      option.style.display = ""
    })
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasClearSearchTarget)
      this.clearSearchTarget.classList.add("hidden")
  }

  // --- Selection ---

  select(event) {
    const option = event.currentTarget
    this._commitSelection(option.dataset.value, option.dataset.label)
    this.close()
    this.triggerTarget.focus()
  }

  _commitSelection(value, label) {
    this.hiddenTarget.value = value

    this.displayTarget.textContent = label
    this.displayTarget.classList.remove("text-foreground-medium")
    this.triggerTarget.dataset.value = "true"

    this.optionTargets.forEach((option) => {
      const isSelected = option.dataset.value === value
      option.setAttribute("aria-selected", isSelected ? "true" : "false")
      if (isSelected) {
        option.classList.remove(this.UNSELECTED_CLASS)
        option.classList.add(...this.SELECTED_CLASSES)
      } else {
        option.classList.remove(...this.SELECTED_CLASSES)
        option.classList.add(this.UNSELECTED_CLASS)
      }
    })

    this.dispatch("change", { detail: { value, label } })
    // Also fire a native `change` on the hidden input so plain form listeners
    // (validations, other Stimulus `change->…` actions) react like a real
    // <select>. The custom `ui--combo-box:change` above is kept for consumers
    // that opt into it (e.g. the rule form's model picker).
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // --- Keyboard navigation ---

  _handleKeydown(event) {
    if (!this._isOpen) return

    const options = this._visibleOptions()

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        this.triggerTarget.focus()
        break
      case "ArrowDown":
        event.preventDefault()
        if (!options.length) return
        this._activeIndex = Math.min(this._activeIndex + 1, options.length - 1)
        this._highlight(options)
        break
      case "ArrowUp":
        event.preventDefault()
        if (!options.length) return
        this._activeIndex = Math.max(this._activeIndex - 1, 0)
        this._highlight(options)
        break
      case "Enter":
        if (this._activeIndex >= 0 && options[this._activeIndex]) {
          event.preventDefault()
          const option = options[this._activeIndex]
          this._commitSelection(option.dataset.value, option.dataset.label)
          this.close()
          this.triggerTarget.focus()
        }
        break
    }
  }

  _visibleOptions() {
    return this.optionTargets.filter((o) => o.style.display !== "none")
  }

  _highlight(options) {
    options.forEach((option, index) => {
      const active = index === this._activeIndex
      option.classList.toggle("bg-input", active)
      if (active) {
        option.scrollIntoView({ block: "nearest" })
        if (this.hasSearchTarget) {
          if (!option.id) option.id = `${this.element.id}-option-${index}`
          this.searchTarget.setAttribute("aria-activedescendant", option.id)
        }
      }
    })
  }

  _clearActive() {
    this.optionTargets.forEach((option) => {
      // Only strip the keyboard highlight from non-selected options, so a
      // selected option keeps its bg-primary styling.
      if (option.getAttribute("aria-selected") !== "true") {
        option.classList.remove("bg-input")
      }
    })
    if (this.hasSearchTarget) {
      this.searchTarget.removeAttribute("aria-activedescendant")
    }
  }

  _handleOutsideClick(event) {
    if (this._isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }
}
